import { useTranslation } from 'react-i18next'
import { Voicemail } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DrilldownVoicemailRow } from '@/types/analyticsDrilldown'
import { formatDateTime, formatDuration } from './formatters'

interface Props {
  rows: DrilldownVoicemailRow[]
  locale: string
  darkMode: boolean
}

export function VoicemailsDrilldownTable({ rows, locale, darkMode }: Props) {
  const { t } = useTranslation()
  const headerBase = cn(
    'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide',
    darkMode ? 'text-slate-400' : 'text-slate-500',
  )
  const cellBase = cn(
    'px-3 py-3 text-sm',
    darkMode ? 'text-slate-200' : 'text-slate-700',
  )
  const rowBorder = darkMode ? 'border-slate-700/60' : 'border-slate-200/70'

  return (
    <div
      className={cn(
        'rounded-xl border overflow-hidden',
        darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white',
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className={cn(darkMode ? 'bg-slate-800/80' : 'bg-slate-50')}>
            <tr className={cn('border-b', rowBorder)}>
              <th className={cn(headerBase, 'pl-4 w-12')}></th>
              <th className={headerBase}>{t('manager.analytics.drilldown.vm.received')}</th>
              <th className={headerBase}>{t('manager.analytics.drilldown.vm.listened')}</th>
              <th className={headerBase}>{t('manager.analytics.drilldown.vm.from')}</th>
              <th className={headerBase}>{t('manager.analytics.drilldown.vm.agent')}</th>
              <th className={cn(headerBase, 'text-right')}>
                {t('manager.analytics.drilldown.vm.duration')}
              </th>
              <th className={cn(headerBase, 'pr-4')}>
                {t('manager.analytics.drilldown.vm.transcript')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={cn(
                  'border-b last:border-b-0 transition-colors',
                  rowBorder,
                  darkMode ? 'hover:bg-slate-800/60' : 'hover:bg-slate-50',
                )}
              >
                <td className={cn(cellBase, 'pl-4')}>
                  <Voicemail className="h-4 w-4 text-indigo-500" />
                </td>
                <td className={cn(cellBase, 'tabular-nums')}>
                  {formatDateTime(row.created_at, locale)}
                </td>
                <td className={cn(cellBase, 'tabular-nums')}>
                  {formatDateTime(row.listened_at, locale)}
                </td>
                <td className={cellBase}>{row.from_number}</td>
                <td className={cellBase}>
                  {row.listened_by?.name ?? (
                    <span className={cn(darkMode ? 'text-slate-500' : 'text-slate-400')}>–</span>
                  )}
                </td>
                <td className={cn(cellBase, 'text-right tabular-nums')}>
                  {formatDuration(row.recording_duration)}
                </td>
                <td
                  className={cn(cellBase, 'pr-4 max-w-md truncate text-xs', darkMode ? 'text-slate-400' : 'text-slate-500')}
                  title={row.transcription_preview ?? ''}
                >
                  {row.transcription_preview ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
